// Client/src/pages/digi/Invoice/TemplateDesigner/controls.jsx
//
// Small labelled form controls the inspector panels are built from. Every
// control has a visible label (or an aria-label for icon-only buttons) and
// keeps the app's input styling (DESIGN.md: hairline border, 4 px radius,
// blue focus ring).

import React, { useEffect, useState } from "react";
import FeatherIcon from "feather-icons-react";
import { COLOR_TOKENS, TOKEN_LABELS } from "./designModel";

let fallbackId = 0;
// React 17 has no useId; a stable per-mount id is enough for label/for pairs.
function useFieldId(prefix) {
  const [id] = useState(() => `${prefix}-${++fallbackId}`);
  return id;
}

export function IconButton({ icon, label, onClick, active, disabled, danger, size = 15, className = "", ...rest }) {
  return (
    <button
      type="button"
      className={`td-icon-btn${active ? " is-active" : ""}${danger ? " is-danger" : ""} ${className}`}
      aria-label={label}
      title={label}
      aria-pressed={active === undefined ? undefined : !!active}
      onClick={onClick}
      disabled={disabled}
      {...rest}
    >
      <FeatherIcon icon={icon} size={size} />
    </button>
  );
}

export function Section({ title, children, defaultOpen = true, actions }) {
  const [open, setOpen] = useState(defaultOpen);
  const id = useFieldId("sec");
  return (
    <section className="td-section">
      <div className="td-section__head">
        <button type="button" className="td-section__toggle" aria-expanded={open} aria-controls={id} onClick={() => setOpen((o) => !o)}>
          <FeatherIcon icon={open ? "chevron-down" : "chevron-right"} size={14} />
          <span>{title}</span>
        </button>
        {actions}
      </div>
      {open && (
        <div className="td-section__body" id={id}>
          {children}
        </div>
      )}
    </section>
  );
}

export function Field({ label, hint, children, inline }) {
  const id = useFieldId("fld");
  const child = React.isValidElement(children) ? React.cloneElement(children, { id: children.props.id || id }) : children;
  return (
    <div className={`td-field${inline ? " td-field--inline" : ""}`}>
      <label className="td-label" htmlFor={id}>
        {label}
      </label>
      {child}
      {hint && <div className="td-hint">{hint}</div>}
    </div>
  );
}

/**
 * Number input that commits on blur/Enter (and on arrow steps), so a value
 * half-typed ("1") doesn't jump the design around. Empty → `onChange(undefined)`
 * when `allowEmpty`.
 */
export function NumberInput({ id, value, onChange, min, max, step = 1, unit, allowEmpty, placeholder, ariaLabel }) {
  const [text, setText] = useState(value === undefined || value === null ? "" : String(value));
  useEffect(() => {
    setText(value === undefined || value === null ? "" : String(value));
  }, [value]);
  const commit = (raw) => {
    if (raw === "" && allowEmpty) return onChange(undefined);
    let n = Number(raw);
    if (!Number.isFinite(n)) return setText(value === undefined ? "" : String(value));
    if (min !== undefined) n = Math.max(min, n);
    if (max !== undefined) n = Math.min(max, n);
    n = Math.round(n * 100) / 100;
    setText(String(n));
    if (n !== value) onChange(n);
  };
  return (
    <div className="td-num">
      <input
        id={id}
        className="td-input"
        type="number"
        inputMode="decimal"
        value={text}
        min={min}
        max={max}
        step={step}
        placeholder={placeholder}
        aria-label={ariaLabel}
        onChange={(e) => setText(e.target.value)}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit(e.currentTarget.value);
          if (e.key === "ArrowUp" || e.key === "ArrowDown") {
            e.preventDefault();
            const base = Number(text) || 0;
            const delta = (e.key === "ArrowUp" ? 1 : -1) * step * (e.shiftKey ? 10 : 1);
            commit(String(base + delta));
          }
        }}
      />
      {unit && <span className="td-num__unit">{unit}</span>}
    </div>
  );
}

export function TextInput({ id, value, onChange, placeholder, maxLength, ariaLabel, multiline, rows = 3, onCommit }) {
  const Tag = multiline ? "textarea" : "input";
  return (
    <Tag
      id={id}
      className="td-input"
      value={value || ""}
      placeholder={placeholder}
      maxLength={maxLength}
      aria-label={ariaLabel}
      rows={multiline ? rows : undefined}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onCommit ? (e) => onCommit(e.target.value) : undefined}
    />
  );
}

export function SelectInput({ id, value, onChange, options, ariaLabel, placeholder }) {
  return (
    <select id={id} className="td-input td-select" value={value ?? ""} aria-label={ariaLabel} onChange={(e) => onChange(e.target.value)}>
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export function Toggle({ id, checked, onChange, label }) {
  return (
    <label className="td-toggle" htmlFor={id}>
      <input id={id} type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="td-toggle__track" aria-hidden="true" />
      <span>{label}</span>
    </label>
  );
}

/** Icon/label buttons acting as a radio group (alignment, borders, ...). */
export function Segmented({ value, onChange, options, ariaLabel }) {
  return (
    <div className="td-seg" role="radiogroup" aria-label={ariaLabel}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          className={`td-seg__btn${value === o.value ? " is-active" : ""}`}
          title={o.label}
          aria-label={o.icon ? o.label : undefined}
          onClick={() => onChange(value === o.value && o.toggle ? undefined : o.value)}
        >
          {o.icon ? <FeatherIcon icon={o.icon} size={14} /> : o.label}
        </button>
      ))}
    </div>
  );
}

const HEX = /^#[0-9a-f]{6}$/i;

/**
 * Colour picker: the template palette's tokens first (so a palette change
 * recolours everything using them), then a custom colour. `allowNone` adds a
 * "none" swatch that clears the value.
 */
export function ColorInput({ id, value, onChange, palette, allowNone = true, ariaLabel }) {
  const isToken = COLOR_TOKENS.includes(value);
  const hex = isToken ? palette?.[value] : HEX.test(value || "") ? value : "";
  const [custom, setCustom] = useState(hex || "#000000");
  useEffect(() => {
    if (!isToken && HEX.test(value || "")) setCustom(value);
  }, [value, isToken]);
  return (
    <div className="td-color" id={id} role="group" aria-label={ariaLabel}>
      {allowNone && (
        <button
          type="button"
          className={`td-swatch td-swatch--none${!value ? " is-active" : ""}`}
          title="None"
          aria-label="No colour"
          aria-pressed={!value}
          onClick={() => onChange(undefined)}
        />
      )}
      {COLOR_TOKENS.map((tk) => (
        <button
          key={tk}
          type="button"
          className={`td-swatch${value === tk ? " is-active" : ""}`}
          style={{ background: palette?.[tk] }}
          title={`${TOKEN_LABELS[tk]} (palette)`}
          aria-label={`${TOKEN_LABELS[tk]} palette colour`}
          aria-pressed={value === tk}
          onClick={() => onChange(tk)}
        />
      ))}
      <label className={`td-swatch td-swatch--custom${!isToken && value ? " is-active" : ""}`} title="Custom colour">
        <input
          type="color"
          aria-label="Custom colour"
          value={custom}
          onChange={(e) => {
            setCustom(e.target.value);
            onChange(e.target.value.toLowerCase());
          }}
        />
        <FeatherIcon icon="droplet" size={12} />
      </label>
      <input
        className="td-input td-color__hex"
        aria-label="Hex colour"
        value={isToken ? TOKEN_LABELS[value] : value || ""}
        placeholder="#rrggbb"
        readOnly={isToken}
        onChange={(e) => {
          const v = e.target.value.trim();
          if (HEX.test(v)) onChange(v.toLowerCase());
        }}
        onFocus={(e) => isToken && e.target.select()}
      />
    </div>
  );
}

/** Four mm values (top, right, bottom, left). */
export function BoxInput({ value, onChange, max = 30, labels = ["Top", "Right", "Bottom", "Left"] }) {
  const v = value || [0, 0, 0, 0];
  return (
    <div className="td-box">
      {labels.map((l, i) => (
        <div key={l} className="td-box__cell">
          <NumberInput
            ariaLabel={l}
            value={v[i]}
            min={0}
            max={max}
            step={0.5}
            onChange={(n) => {
              const next = [...v];
              next[i] = n ?? 0;
              onChange(next.every((x) => !x) ? undefined : next);
            }}
          />
          <span className="td-box__label">{l[0]}</span>
        </div>
      ))}
    </div>
  );
}
